import { createApi } from '@reduxjs/toolkit/query/react'
import baseQuery from './baseQuery'

/**
 * Internal Alerts panel (Reports & Analytics → Internal Alerts) — WhatsApp, SMS and Email tabs.
 * Backend: proship_mongo `/api/alerts/internal/*` — ROLE_PROSHIPADMIN + INTERNAL_ALERTS module.
 * Endpoints return the DTO directly (not wrapped in GenericAPIResponse), hence `res?.data || res`.
 * Definitions carry `channel`; the page filters client-side, history passes `channel` to the backend.
 *
 * Reads hit Proship's own small collections (the ingested copy), never the shared WhatsApp audit DB — except
 * `getLive`, the explicit "look in the audit DB" action (one template, ≤ 31 days, ≤ 500 rows).
 * Mutations (Phase 1) return the saved definition; a 409 means either "already a recipient" or a lost
 * optimistic-lock race ("changed by someone else") — the message says which.
 */
const apiBase = 'alerts/internal'
const enc = encodeURIComponent

/**
 * Same wrapper as stuckShipmentsApiSlice: the shared baseQuery throws the raw axios error and RTK Query keeps only its
 * message, so backend texts ("INTERNAL_ALERTS access is required…", the 409 skippedReason) never reached the page.
 * Return an RTK-style error so `error.status` and `error.data` survive.
 */
const alertsBaseQuery = async (args, api, extra) => {
  try {
    return await baseQuery(args, api, extra)
  } catch (err) {
    const status = err?.response?.status
    const data = err?.response?.data
    return { error: { status, data, message: data?.message || data?.skippedReason || err?.message || 'Request failed' } }
  }
}

/** Drop empty filter values so the query string carries only what the user set (and arrays only when non-empty). */
const clean = (o = {}) => Object.fromEntries(
  Object.entries(o).filter(([, v]) => v !== undefined && v !== null && v !== '' && !(Array.isArray(v) && v.length === 0))
)

const unwrap = (res) => res?.data || res
const CONFIG_TAGS = ['Definitions', 'Health', 'Changelog']

export const internalAlertsApi = createApi({
  reducerPath: 'internalAlertsApi',
  baseQuery: alertsBaseQuery,
  keepUnusedDataFor: 5 * 60,
  tagTypes: ['Definitions', 'Health', 'Dispatches', 'Changelog'],
  endpoints: (builder) => ({

    // ---- reads ----------------------------------------------------------------------------------------------

    // Catalogue: every definition with recipients, lastRun and health — one call renders the Catalogue tab.
    getDefinitions: builder.query({
      query: () => ({ url: `${apiBase}/definitions`, type: 'GET' }),
      transformResponse: unwrap,
      providesTags: ['Definitions'],
    }),

    getDefinition: builder.query({
      query: (templateName) => ({ url: `${apiBase}/definitions/${enc(templateName)}`, type: 'GET' }),
      transformResponse: unwrap,
      providesTags: ['Definitions'],
    }),

    // { templateNames[], from, to, channel (WHATSAPP|SMS|EMAIL), sourceSystem, recipient (mobile or email), status, currentStatus, runKey, page (1-based), pageSize }
    getDispatches: builder.query({
      query: (q) => ({ url: `${apiBase}/dispatches`, data: clean(q), type: 'GET' }),
      transformResponse: unwrap,
      keepUnusedDataFor: 60,
      providesTags: ['Dispatches'],
    }),

    // Same filters, grouped by run (template × source × 10-minute bucket).
    getRuns: builder.query({
      query: (q) => ({ url: `${apiBase}/dispatches/runs`, data: clean(q), type: 'GET' }),
      transformResponse: unwrap,
      keepUnusedDataFor: 60,
      providesTags: ['Dispatches'],
    }),

    // { templateName, from, to, limit } — direct audit-DB read, explicit user action only.
    getLive: builder.query({
      query: (q) => ({ url: `${apiBase}/dispatches/live`, data: clean(q), type: 'GET' }),
      transformResponse: unwrap,
      keepUnusedDataFor: 0,
    }),

    getHealth: builder.query({
      query: () => ({ url: `${apiBase}/health`, type: 'GET' }),
      transformResponse: unwrap,
      providesTags: ['Health'],
    }),

    // { templateName, page (1-based), pageSize (≤100) } → { rows: [{ action, actor, at, before, after, reason }], total }
    getChangelog: builder.query({
      query: ({ templateName, page, pageSize }) => ({ url: `${apiBase}/definitions/${enc(templateName)}/changelog`, data: clean({ page, pageSize }), type: 'GET' }),
      transformResponse: unwrap,
      keepUnusedDataFor: 60,
      providesTags: ['Changelog'],
    }),

    // Prefill helper: is this mobile / email a Proship user? → { mobile (E.164), email, valid, found, displayName }
    lookupRecipient: builder.query({
      query: (q) => ({ url: `${apiBase}/recipients/lookup`, data: { q }, type: 'GET' }),
      transformResponse: unwrap,
      keepUnusedDataFor: 30,
    }),

    // Refresh now → 200 IngestRunResult, or 409 with skippedReason (already running / too soon).
    runIngest: builder.mutation({
      query: () => ({ url: `${apiBase}/ingest/run`, data: {}, type: 'POST' }),
      transformResponse: unwrap,
      invalidatesTags: ['Definitions', 'Health', 'Dispatches'],
    }),

    // ---- Phase 1: control -----------------------------------------------------------------------------------

    // { templateName, displayName?, description?, ownerEmail?, expectedCadence?, clearCadence?, messagesPerRun?, reason? }
    updateDefinition: builder.mutation({
      query: ({ templateName, ...body }) => ({ url: `${apiBase}/definitions/${enc(templateName)}`, data: body, type: 'PUT' }),
      transformResponse: unwrap,
      invalidatesTags: CONFIG_TAGS,
    }),

    setEnabled: builder.mutation({
      query: ({ templateName, enabled, reason }) => ({ url: `${apiBase}/definitions/${enc(templateName)}/enabled`, data: { enabled, reason }, type: 'PUT' }),
      transformResponse: unwrap,
      invalidatesTags: CONFIG_TAGS,
    }),

    // WhatsApp / SMS alerts take { mobile } (any Indian form; stored E.164); EMAIL alerts take { email, headerType: TO|CC|BCC }.
    // Re-adding an inactive recipient reactivates them; 409 if already active.
    addRecipient: builder.mutation({
      query: ({ templateName, mobile, email, headerType, displayName, reason }) => ({ url: `${apiBase}/definitions/${enc(templateName)}/recipients`, data: { mobile, email, headerType, displayName, reason }, type: 'POST' }),
      transformResponse: unwrap,
      invalidatesTags: CONFIG_TAGS,
    }),

    // `key` = the recipient's mobile (WhatsApp / SMS) or email (EMAIL); `mobile` is accepted for older callers.
    deactivateRecipient: builder.mutation({
      query: ({ templateName, key, mobile, reason }) => ({
        url: `${apiBase}/definitions/${enc(templateName)}/recipients/${enc(key || mobile)}${reason ? `?reason=${enc(reason)}` : ''}`,
        data: {}, type: 'DELETE',
      }),
      transformResponse: unwrap,
      invalidatesTags: CONFIG_TAGS,
    }),

    reactivateRecipient: builder.mutation({
      query: ({ templateName, key, mobile }) => ({ url: `${apiBase}/definitions/${enc(templateName)}/recipients/${enc(key || mobile)}/reactivate`, data: {}, type: 'POST' }),
      transformResponse: unwrap,
      invalidatesTags: CONFIG_TAGS,
    }),

    // ---- Phase 2: Proship-computed alerts ----------------------------------------------------------------------
    // Only Preview is in the panel. The cutover switch (PUT …/sender), Send now, Send test, snapshot run/status and
    // the backfill are admin-API-only (token + curl, see the design doc runbook) — rare, one-off operations.

    // Compute + render today's message per recipient; sends nothing. 422 when the snapshot is missing.
    previewAlert: builder.mutation({
      query: (templateName) => ({ url: `${apiBase}/definitions/${enc(templateName)}/preview`, data: {}, type: 'POST' }),
      transformResponse: unwrap,
    }),
  }),
})

export const {
  useGetDefinitionsQuery,
  useGetDefinitionQuery,
  useGetDispatchesQuery,
  useGetRunsQuery,
  useGetLiveQuery,
  useGetHealthQuery,
  useGetChangelogQuery,
  useLookupRecipientQuery,
  useRunIngestMutation,
  useUpdateDefinitionMutation,
  useSetEnabledMutation,
  useAddRecipientMutation,
  useDeactivateRecipientMutation,
  useReactivateRecipientMutation,
  usePreviewAlertMutation,
} = internalAlertsApi
